// Safari/iOS install path (JW9, web/jobs.md "Architecture reality" §4). When
// the running context CAN'T receive call pushes, we tell the user how to fix it
// instead of leaving them to silently miss calls:
//   - iOS Safari tab  → "Add Merkury to your Home Screen" with the share-sheet
//     steps (a plain iOS tab gets no Web Push at all — installing is the ONLY
//     path that can ring).
//   - notifications denied (desktop/macOS, or an installed iOS app) → point at
//     enabling notifications.
//
// Two surfaces share the same copy via `InstallHintBody`: the onboarding
// Notifications step (which shows the iOS steps INSTEAD of a doomed permission
// prompt) and `InstallHint`, a dismissible banner the orchestrator mounts for
// signed-in users in that state (see this job's report for the mount point).
// The block-reason detection lives in push/notificationPermission.ts as pure,
// seam-mockable helpers — this file is presentation only.

import { useState, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';
import {
  callPushBlockReason,
  type InstallHintReason,
  type PlatformSeams,
} from '../../push/notificationPermission.js';

const DISMISS_KEY = 'merkury.installHint.dismissed';

function readDismissed(): string | null {
  try {
    return globalThis.localStorage?.getItem(DISMISS_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeDismissed(reason: string): void {
  try {
    globalThis.localStorage?.setItem(DISMISS_KEY, reason);
  } catch {
    // Storage can be unavailable (private mode); a non-persisted dismiss is fine.
  }
}

/**
 * The localized guidance for a block reason — shared by the onboarding step and
 * the signed-in banner so the copy can never diverge. Presentation only.
 */
export function InstallHintBody({ reason }: { reason: InstallHintReason }): ReactNode {
  const t = useT();
  if (reason === 'ios-install') {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-ink">{t('installHint.ios.title')}</p>
        <p className="text-sm text-muted">{t('installHint.ios.body')}</p>
        <ol className="flex list-decimal flex-col gap-1 pl-5 text-sm text-muted">
          <li>{t('installHint.ios.step1')}</li>
          <li>{t('installHint.ios.step2')}</li>
          <li>{t('installHint.ios.step3')}</li>
        </ol>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-ink">{t('installHint.denied.title')}</p>
      <p className="text-sm text-muted">{t('installHint.denied.body')}</p>
    </div>
  );
}

const DismissIcon = (
  <svg
    viewBox="0 0 24 24"
    className="h-[18px] w-[18px]"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

/**
 * Dismissible banner for signed-in users whose context can't receive call
 * pushes. Renders nothing when the context is fine, native, or the user already
 * dismissed this reason (persisted per-reason, so a resolved state or a new
 * reason surfaces again but the same one never nags). Tests drive it via
 * `reason`/`seams`; production leaves both unset and lets it self-detect.
 */
export function InstallHint({
  reason: reasonProp,
  seams,
  onDismiss,
}: {
  /** Force a reason (or `null` to hide). Unset ⇒ self-detect from `seams`. */
  reason?: InstallHintReason | null;
  seams?: PlatformSeams;
  onDismiss?: () => void;
} = {}): ReactNode {
  const t = useT();
  const reason = reasonProp !== undefined ? reasonProp : callPushBlockReason(seams);
  const [dismissed, setDismissed] = useState(() => reason !== null && readDismissed() === reason);
  if (!reason || dismissed) return null;
  return (
    <div
      role="status"
      data-testid="install-hint"
      className="flex items-start justify-between gap-3 rounded-xl border border-edge bg-surface p-3"
    >
      <InstallHintBody reason={reason} />
      <button
        type="button"
        aria-label={t('installHint.dismiss')}
        onClick={() => {
          writeDismissed(reason);
          setDismissed(true);
          onDismiss?.();
        }}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-raised hover:text-ink"
      >
        {DismissIcon}
      </button>
    </div>
  );
}
